import React, { useCallback, useEffect, useState } from 'react';
import { PageContentLayout, PageHeaderLayout } from '../../../shared/layout';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  TagsOutlined,
  PlusOutlined,
  MinusCircleOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import {
  AutoComplete,
  Button,
  Card,
  Col,
  Collapse,
  Divider,
  Form,
  Input,
  message,
  Radio,
  Row,
  Select,
  Skeleton,
  Switch,
  Tooltip,
  Typography,
  Modal,
  Table,
  Space,
  Alert,
} from 'antd';
import { v4 } from 'uuid';
import AceEditor from 'react-ace';
import { useLazyQuery, useMutation } from 'react-apollo';
import FIND_FRICTIONLESS_TAG from '../../../../core/GraphQl/Queries/FIND_FRICTIONLESS_TAG';
import CREATE_FRICTIONLESS_TAG from '../../../../core/GraphQl/Mutations/CREATE_FRICTIONLESS_TAG';
import UPDATE_FRICTIONLESS_TAG from '../../../../core/GraphQl/Mutations/UPDATE_FRICTIONLESS_TAG';
import { determineUserAccess } from '../../../../core/permissions/userLevelAccess';
import { TableColumnTitle } from '../../../shared/globalStyling/styledText';
import { fontSize } from '../../Tasks/configs/designTokens';
import AppSpinner from '../../Tasks/components/shared/AppSpinner';
import '../styles/index.scss';
import { resolveTagCodeUrl, validateTagCode } from '../../../shared/urlValidator';

import 'ace-builds/src-noconflict/mode-javascript';
import 'ace-builds/src-noconflict/theme-tomorrow_night';
import ace from 'ace-builds/src-noconflict/ace';

ace.config.set('useWorker', false);

const { Text } = Typography;
const { Option } = Select;
const { Link } = Typography;

const ToggleFieldWithDescription = ({
  label,
  description,
  checked,
  onChange,
  disabled,
  disabledTooltip,
}) => {
  const switchEl = (
    <Switch
      checked={checked}
      onChange={onChange}
      className="tags-manager__toggle-field-switch"
      disabled={disabled}
    />
  );

  return (
    <div
      className={`tags-manager__toggle-field${disabled ? ' tags-manager__toggle-field--disabled' : ''}`}
    >
      <div className="tags-manager__toggle-field-content">
        <div className="tags-manager__toggle-field-label">{label}</div>
        <p className="tags-manager__toggle-field-description">{description}</p>
      </div>
      {disabled && disabledTooltip ? (
        <Tooltip title={disabledTooltip} trigger={['hover', 'click']}>
          <span className="tags-manager__toggle-field-disabled-wrap">{switchEl}</span>
        </Tooltip>
      ) : (
        switchEl
      )}
    </div>
  );
};

const TagsManagerView = ({
  onSubmit,
  advertisers,
  currentOrgId,
  currentUser,
  headerSubmit,
  tagType,
  carts,
  orgs,
  isUpdate = false,
  platforms,
  onCard = true,
  details, // If is update details value is populated
  measurementTags, // Exclusive Input for Publisher Domain Manager
  detectCart,
  cartDetectionData,
  cartDetectionLoading,
  onCancel,
}) => {
  const userIsTrafficker =
    currentUser && currentUser.permission && currentUser.permission.isTrafficker;
  const detailsThirdPartyTag = details && details.thirdPartyTag;
  const [formTag] = Form.useForm();
  const [macrosVisible, setMacrosVisible] = useState(false);
  const [form] = Form.useForm();
  const [ecommerceSetup, setEcommerceSetup] = useState(null);
  const [isMultiTagType, setIsMultiTagType] = useState('no');
  const [formattedThirdPartyTag, setFormattedThirdPartyTag] = useState(
    detailsThirdPartyTag
      ? detailsThirdPartyTag
      : {
          onTransaction: [
            {
              type: 'script',
              tag: '',
            },
          ],
        }
  );
  const [siteUrl, setSiteUrl] = useState('');
  const [detectedCarts, setDetectedCarts] = useState([]);
  const [validUrl, setValidUrl] = useState(false);
  const [selectedCart, setSelectedCart] = useState();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [signupsSetup, setSignupsSetup] = useState(false);
  const [dspTrackingSetup, setDspTrackingSetup] = useState(false);
  const [tagOverrides, setTagOverrides] = useState([{ id: v4(), field: '', value: '' }]);
  const [partiallyFilledRows, setPartiallyFilledRows] = useState({});
  const [codeLoadedFromRepo, setCodeLoadedFromRepo] = useState(false);
  const [hasAppIdOverride, setHasAppIdOverride] = useState(false);
  const [isCodeEditorChange, setIsCodeEditorChange] = useState(false);
  const [customCode, setCustomCode] = useState(
    `// Custom code example
function customTracking() {
  // Your custom tracking code here
  console.log('Custom tracking initialized');
}

// You can access tag data and trigger custom events
// This code will execute when the tag is loaded on the site`
  );

  // State for frictionless tag data with retry mechanism
  const [frictionlessTagData, setFrictionlessTagData] = useState(null);
  const [frictionlessRetryCount, setFrictionlessRetryCount] = useState(0);
  const [isTagDisabled, setIsTagDisabled] = useState(false);
  const MAX_FRICTIONLESS_RETRIES = 3;

  const OVERRIDE_FIELD_OPTIONS = [
    'environment',
    'version',
    's2.pv',
    's2.tr',
    's3.pv',
    's3.tr',
    'plugin',
    'conversionId',
    'conversionLabel',
    'tagId',
  ];

  // GraphQL hooks for frictionless tags
  const [findFrictionlessTag, { loading: frictionlessTagLoading }] = useLazyQuery(
    FIND_FRICTIONLESS_TAG,
    {
      fetchPolicy: 'network-only',
      onCompleted: (data) => {
        setFrictionlessTagData(data);
        setFrictionlessRetryCount(0);
      },
      onError: () => {
        setFrictionlessRetryCount((prev) => prev + 1);
      },
    }
  );

  const [createFrictionlessTag] = useMutation(CREATE_FRICTIONLESS_TAG);
  const [updateFrictionlessTag] = useMutation(UPDATE_FRICTIONLESS_TAG);

  // User permission check
  const getPermission = currentUser && currentUser.permission;
  const orgConfig = determineUserAccess(getPermission);
  // Helper function to validate no appId override
  const validateNoAppIdOverride = (overrides) => {
    return overrides.some((override) => override.field && override.field.toLowerCase() === 'appid');
  };

  // Check for appId override whenever tagOverrides changes
  useEffect(() => {
    setHasAppIdOverride(validateNoAppIdOverride(tagOverrides));
  }, [tagOverrides]);

  // Helper function to parse code and extract override values
  const parseCodeForOverrides = (code) => {
    if (!code) return [];

    try {
      // Look for patterns like: window.overrides = {} or var overrides = {...}
      const overridesPattern = /(?:var|let|const)?\s*overrides\s*=\s*\{([^}]+)\}/s;
      const match = code.match(overridesPattern);

      if (match && match[1]) {
        const overridesContent = match[1];
        // Updated pattern to handle quoted field names with special characters (dots, hyphens, etc.)
        // Pattern matches: "field.name": "value" or "field-name": "value"
        const fieldPattern = /"([^"]+)"\s*:\s*"([^"]+)"/g;
        const fields = [];
        let fieldMatch;

        while ((fieldMatch = fieldPattern.exec(overridesContent)) !== null) {
          fields.push({
            id: v4(),
            field: fieldMatch[1],
            value: fieldMatch[2],
          });
        }

        return fields.length > 0 ? fields : [{ id: v4(), field: '', value: '' }];
      }
    } catch (error) {
      console.error('Error parsing code for overrides:', error);
    }

    return [{ id: v4(), field: '', value: '' }];
  };

  // Helper function to generate code from override values
  const generateCodeFromOverrides = useCallback(
    (overrides, appId, isDisabled) => {
      const validOverrides = overrides.filter(
        (override) =>
          override.field && override.field.trim() && override.value && override.value.trim()
      );

      const allOverrides = isDisabled
        ? [{ field: 'enable', value: 'false' }, ...validOverrides]
        : validOverrides;

      if (allOverrides.length === 0) {
        return customCode;
      }

      const sanitizedAppId = appId ? appId.replace(/-/g, '_') : 'app_id';
      const sanitizedFunctionName = `override_${sanitizedAppId}`;
      const overridesObject = allOverrides
        .map((override) => `    "${override.field}": "${override.value}"`)
        .join(',\n');

      return `const ${sanitizedFunctionName} = () => {
  window.overrides = {};

  const overrides = {
${overridesObject}
  };

  window.overrides = overrides;
};

${sanitizedFunctionName}();`;
    },
    [customCode]
  );

  // Helper function to update ONLY the overrides variable in existing code
  // This preserves custom code like console.logs, custom functions, etc.
  const updateOverridesInCode = useCallback((existingCode, overrides, isDisabled) => {
    const validOverrides = overrides.filter(
      (override) =>
        override.field && override.field.trim() && override.value && override.value.trim()
    );

    const allOverrides = isDisabled
      ? [{ field: 'enable', value: 'false' }, ...validOverrides]
      : validOverrides;

    // Build the new overrides object content
    const overridesObject =
      allOverrides.length > 0
        ? allOverrides.map((override) => `    "${override.field}": "${override.value}"`).join(',\n')
        : '';

    // Try to find and replace the overrides variable content
    // Pattern matches: const overrides = { ... } or var overrides = { ... }
    const overridesPattern = /((?:const|let|var)\s+overrides\s*=\s*\{)([^}]*)\}/s;

    if (overridesPattern.test(existingCode)) {
      // Replace only the overrides object content, keeping everything else
      return existingCode.replace(overridesPattern, `$1\n${overridesObject}\n  }`);
    }

    // If pattern not found, return the code unchanged
    return existingCode;
  }, []);
  const [currentAdvertiserDspId, setCurrentAdvertiserDspId] = useState(() => {
    if (!orgs || tagType !== 'Measurement') return null;
    const org = orgs.orgs.find((o) => currentOrgId === o.id);
    return org && org.dataConfig && org.dataConfig.dspAdvertiserId;
  });

  const handleUsePlatform = (cart) => {
    setSelectedCart(cart);
    form.setFieldsValue({ platform: cart });
  };

  // Handle code editor changes and sync to tag overrides
  const handleCodeEditorChange = (value) => {
    setCustomCode(value);

    // Parse the code and update tag overrides if it matches the expected format
    const parsedOverrides = parseCodeForOverrides(value);

    // Only update if we got valid parsed overrides (not just the default empty one)
    if (parsedOverrides && parsedOverrides.length > 0) {
      const hasActualValues = parsedOverrides.some((override) => override.field && override.value);

      if (hasActualValues) {
        // Set flag to prevent infinite loop
        setIsCodeEditorChange(true);
        setTagOverrides(parsedOverrides);
      }
    }
  };

  const showMacrosModal = () => {
    setMacrosVisible(true);
  };

  // Filters for editing of tags by type
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const source = searchParams.get('source');

  if (source) {
    switch (source) {
      case 'impression':
        tagType = 'Impression';
        break;
      case 'publisher':
        tagType = 'Publisher';
        break;
      default:
        tagType = 'Measurement';
        break;
    }
  }

  if (details) {
    if (details.isPublisherDomain) tagType = 'Publisher';
    else if (details.isAdvertiser) tagType = 'Impression';
  }

  const domainLookUp = (orgId) => {
    let domain = null;

    // Add defensive check for orgs data structure
    if (!orgs || !orgs.orgs || !Array.isArray(orgs.orgs)) {
      return 'https://tags.cnna.io';
    }

    const advertiser = orgs.orgs.find((org) => orgId === org.id);

    // Add defensive check for advertiser existence
    if (!advertiser) {
      return 'https://tags.cnna.io';
    }

    // Check if parentOrg exists and find CHANNEL_PARTNER with tagDomain
    const parentChannelPartner =
      advertiser.parentOrg && advertiser.parentOrg.length > 0
        ? advertiser.parentOrg.find(
            (org) => org.level === 'CHANNEL_PARTNER' && org.tagDomain !== null
          )
        : null;

    if (advertiser.level === 'CHANNEL_PARTNER') {
      domain = advertiser.tagDomain;
    } else if (parentChannelPartner) {
      domain = parentChannelPartner.tagDomain;
    }
    if (!domain) {
      domain = 'https://tags.cnna.io';
    }
    return domain;
  };

  const dspAdvertiserId = (orgId) => {
    if (!orgs) return null;
    const org = orgs.orgs.find((org) => orgId === org.id);
    return org && org.dataConfig && org.dataConfig.dspAdvertiserId;
  };

  const orgAdvertiserId = tagType === 'Measurement' ? dspAdvertiserId(currentOrgId) : null;

  // Initial Values for Measurement tag manager form
  let initialValuesMeasurement = {};

  useEffect(() => {
    if (isUpdate && ecommerceSetup === null) {
      setEcommerceSetup(initialValuesMeasurement.ecommerce);
      setSignupsSetup(initialValuesMeasurement.signups || false);
      setDspTrackingSetup(initialValuesMeasurement.dspTracking || false);
    }
  }, [ecommerceSetup, initialValuesMeasurement, isUpdate]);

  useEffect(() => {}, [formattedThirdPartyTag]);

  useEffect(() => {
    setDetectedCarts(
      cartDetectionData &&
        cartDetectionData.detectCart &&
        cartDetectionData.detectCart.detectedCarts
    );

    isValidURL(siteUrl) ? setValidUrl(true) : setValidUrl(false);
  }, [cartDetectionData, detectCart, siteUrl]);

  // Fetch existing frictionless tag code when updating
  useEffect(() => {
    if (currentUser && isUpdate && details && details.appId && !codeLoadedFromRepo) {
      findFrictionlessTag({
        variables: {
          data: {
            query: details.appId,
          },
        },
      });
    }
  }, [currentUser, isUpdate, details, codeLoadedFromRepo, findFrictionlessTag]);

  // Retry mechanism for frictionless tag fetch (handles auth timing issues)
  useEffect(() => {
    if (
      frictionlessRetryCount > 0 &&
      frictionlessRetryCount <= MAX_FRICTIONLESS_RETRIES &&
      currentUser &&
      isUpdate &&
      details &&
      details.appId &&
      !codeLoadedFromRepo
    ) {
      const retryTimer = setTimeout(() => {
        findFrictionlessTag({
          variables: {
            data: {
              query: details.appId,
            },
          },
        });
      }, 1000);

      return () => clearTimeout(retryTimer);
    }
  }, [
    frictionlessRetryCount,
    currentUser,
    isUpdate,
    details,
    codeLoadedFromRepo,
    findFrictionlessTag,
  ]);

  // Parse fetched code and populate tag overrides
  useEffect(() => {
    if (frictionlessTagData && frictionlessTagData.findFrictionlessTag) {
      const tags = frictionlessTagData.findFrictionlessTag;
      if (tags.length > 0) {
        const tag = tags[0];
        if (tag.content) {
          const parsedOverrides = parseCodeForOverrides(tag.content);

          // Detect enable: "false" and sync toggle state
          const hasEnableDisabled = parsedOverrides.some(
            (o) => o.field === 'enable' && o.value === 'false'
          );
          setIsTagDisabled(hasEnableDisabled);

          // Filter enable override from the visible textbox overrides
          const visibleOverrides = parsedOverrides.filter((o) => o.field !== 'enable');
          setTagOverrides(
            visibleOverrides.length > 0 ? visibleOverrides : [{ id: v4(), field: '', value: '' }]
          );
          setCustomCode(tag.content);
          setCodeLoadedFromRepo(true);
        }
      } else if (!codeLoadedFromRepo) {
        setCodeLoadedFromRepo(true);
      }
    }
  }, [frictionlessTagData, codeLoadedFromRepo]);

  // Sync tag overrides with code editor (only when changes come from textboxes or toggle)
  // This updates ONLY the overrides variable, preserving custom code
  useEffect(() => {
    if (codeLoadedFromRepo && !isCodeEditorChange) {
      const hasValidOverrides = tagOverrides.some(
        (override) =>
          override.field && override.field.trim() && override.value && override.value.trim()
      );

      // Check if current code is the default example
      const isDefaultCode =
        customCode.includes('// Custom code example') &&
        customCode.includes('Custom tracking initialized');

      // If code is default example or empty, generate fresh code when user adds overrides or disables tag
      if (isDefaultCode || !customCode || !customCode.trim()) {
        if (hasValidOverrides || isTagDisabled) {
          const appId = form.getFieldValue('appId') || (details && details.appId);
          const generatedCode = generateCodeFromOverrides(tagOverrides, appId, isTagDisabled);
          setCustomCode(generatedCode);
        }
      } else {
        // If code exists and is not default, update only the overrides variable to preserve custom code
        const updatedCode = updateOverridesInCode(customCode, tagOverrides, isTagDisabled);
        setCustomCode(updatedCode);
      }
    }
    // Reset the flag after processing
    if (isCodeEditorChange) {
      setIsCodeEditorChange(false);
    }
  }, [
    tagOverrides,
    isTagDisabled,
    codeLoadedFromRepo,
    details,
    form,
    isCodeEditorChange,
    customCode,
    generateCodeFromOverrides,
    updateOverridesInCode,
  ]);

  const validateURL = (_, value) => {
    if (!value) return Promise.resolve(); // Skip empty (allow if not required)

    try {
      const url = new URL(value);
      const domainPattern = /^([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}$/;
      if (!domainPattern.test(url.hostname)) {
        return Promise.reject('URL must have a valid domain (e.g., example.com)');
      }
      return Promise.resolve();
    } catch (err) {
      return Promise.reject('Please enter a valid URL');
    }
  };

  const isValidURL = (string) => {
    try {
      const url = new URL(string);
      const domainPattern = /^([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}$/;
      if (!domainPattern.test(url.hostname)) {
        return Promise.reject('URL must have a valid domain (e.g., example.com)');
      }
      return true;
    } catch (_) {
      return false;
    }
  };

  const handleSiteScan = async () => {
    detectCart({
      variables: {
        url: siteUrl,
      },
    });
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);

      // Check for appId override before proceeding
      if (hasAppIdOverride) {
        message.error(
          'Cannot use "appId" as an override field name. Please use a different field name.'
        );
        setIsSubmitting(false);
        return;
      }

      const values = await form.validateFields();
      await formTag.validateFields();

      // Guard: DSP-level tracking requires an Ad Provider Id configured in org settings.
      if (values.dspTracking && !values.orgAdvertiserId) {
        message.error('DSP-level tracking requires an Ad Provider Id configured in org settings.');
        setIsSubmitting(false);
        return;
      }

      // Filter tagOverrides to only include entries with both field and value
      const validTagOverrides = tagOverrides.filter(
        (override) =>
          override.field && override.field.trim() && override.value && override.value.trim()
      );

      // Check if code is the default example code
      const isDefaultCode =
        customCode.includes('// Custom code example') ||
        customCode.includes('Custom tracking initialized');

      // Check if we have custom code, valid overrides, or disabled state to save to frictionless repo
      const hasCustomCode = customCode && customCode.trim() && !isDefaultCode;
      const hasValidOverrides = validTagOverrides.length > 0;

      // Prepare submission data with advanced options
      const submissionData = {
        ...values,
        thirdPartyTag: formattedThirdPartyTag,
        // Include advanced options for backend processing
        tagOverrides: validTagOverrides.length > 0 ? validTagOverrides : undefined,
        customCode: customCode && customCode.trim() ? customCode : undefined,
      };

      // First, submit the main tag (measurement/publisher/impression tag)
      await onSubmit(submissionData);

      // Then handle frictionless tag creation/update if there is custom code, valid overrides, or disabled state
      if ((hasCustomCode || hasValidOverrides || isTagDisabled) && tagType === 'Measurement') {
        const appId = values.appId || (details && details.appId);

        if (appId) {
          // Transform tag overrides to data object
          // If there are no overrides but there is custom code, pass an empty object
          const overrideData = {};
          if (isTagDisabled) {
            overrideData['enable'] = 'false';
          }
          validTagOverrides.forEach((override) => {
            overrideData[override.field] = override.value;
          });

          // Extract user info for commit messages
          const userInfo = currentUser
            ? {
                username: currentUser.username,
                email: currentUser.email,
              }
            : undefined;

          try {
            let result;

            // Try to update first if we're in update mode
            if (isUpdate) {
              const updateResult = await updateFrictionlessTag({
                variables: {
                  data: {
                    appId: appId,
                    data: overrideData,
                    customCode: customCode && customCode.trim() ? customCode : undefined,
                    userInfo: userInfo,
                  },
                },
              });

              if (updateResult.data && updateResult.data.updateFrictionlessTag) {
                result = updateResult.data.updateFrictionlessTag;

                // If update failed because tag doesn't exist, try to create it
                if (!result.success && result.message.includes('does not exist')) {
                  const createResult = await createFrictionlessTag({
                    variables: {
                      data: {
                        appId: appId,
                        data: overrideData,
                        customCode: customCode && customCode.trim() ? customCode : undefined,
                        userInfo: userInfo,
                      },
                    },
                  });

                  if (createResult.data && createResult.data.createFrictionlessTag) {
                    result = createResult.data.createFrictionlessTag;
                  }
                }
              }
            } else {
              // Create mode
              const createResult = await createFrictionlessTag({
                variables: {
                  data: {
                    appId: appId,
                    data: overrideData,
                    customCode: customCode && customCode.trim() ? customCode : undefined,
                    userInfo: userInfo,
                  },
                },
              });

              if (createResult.data && createResult.data.createFrictionlessTag) {
                result = createResult.data.createFrictionlessTag;
              }
            }

            // Show result message
            if (result) {
              if (result.success) {
                message.success('Frictionless tag saved successfully');
              } else {
                message.warning(`Frictionless tag: ${result.message}`);
              }
            }
          } catch (frictionlessError) {
            console.error('Frictionless tag error:', frictionlessError);
            message.warning('Tag saved but frictionless tag operation encountered an issue');
          }
        }
      }
      message.success(isUpdate ? 'Tag Updated' : 'Tag Created');
      form.resetFields();
    } catch (error) {
      console.log(error);
      if (error && error.errorFields) {
        message.error('Please fix the validation errors in the form before submitting.');
      } else {
        const errorMessage = error && error.message ? error.message : 'Unknown error occurred';
        message.error(`Failed to ${isUpdate ? 'update' : 'create'} tag: ${errorMessage}`);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const macrosData = {
    onTransaction: [
      {
        key: '{transaction_id}',
        description: 'Unique identifier for the transaction.',
      },
      {
        key: '{transaction_total}',
        description: 'Total value of the transaction.',
      },
      {
        key: '{transaction_tax}',
        description: 'Tax amount applied to the transaction.',
      },
      {
        key: '{transaction_shipping}',
        description: 'Shipping cost of the transaction.',
      },
      {
        key: '{transaction_city}',
        description: 'City where the transaction occurred.',
      },
      {
        key: '{transaction_state}',
        description: 'State where the transaction occurred.',
      },
      {
        key: '{transaction_country}',
        description: 'Country where the transaction occurred.',
      },
      {
        key: '{transaction_currency}',
        description: 'Currency used for the transaction.',
      },
      {
        key: '{transaction_userId}',
        description: 'User ID associated with the transaction.',
      },
    ],
    onAddToCart: [
      { key: '{cart_id}', description: 'Unique identifier for the cart.' },
      {
        key: '{cart_name}',
        description: 'Name of the product added to the cart.',
      },
      { key: '{cart_category}', description: 'Category of the product added.' },
      {
        key: '{cart_unitPrice}',
        description: 'Price per unit of the product.',
      },
      { key: '{cart_quantity}', description: 'Quantity of the product added.' },
      {
        key: '{cart_currency}',
        description: 'Currency used for the cart transaction.',
      },
      {
        key: '{cart_userId}',
        description: 'User ID associated with the cart.',
      },
    ],
    onRemoveFromCart: [
      { key: '{cart_id}', description: 'Unique identifier for the cart.' },
      {
        key: '{cart_name}',
        description: 'Name of the product removed from the cart.',
      },
      {
        key: '{cart_category}',
        description: 'Category of the product removed.',
      },
      {
        key: '{cart_unitPrice}',
        description: 'Price per unit of the product.',
      },
      {
        key: '{cart_quantity}',
        description: 'Quantity of the product removed.',
      },
      {
        key: '{cart_currency}',
        description: 'Currency used for the cart transaction.',
      },
      {
        key: '{cart_userId}',
        description: 'User ID associated with the cart.',
      },
    ],
  };

  const [selectedEvent, setSelectedEvent] = useState('onTransaction');

  const handleChange = (value) => {
    setSelectedEvent(value);
  };

  const columns = [
    {
      title: <TableColumnTitle text={'Macro'} />,
      dataIndex: 'key',
      key: 'key',
      render: (text) => <Link onClick={() => navigator.clipboard.writeText(text)}>{text}</Link>,
    },
    {
      title: <TableColumnTitle text={'Description'} />,
      dataIndex: 'description',
      key: 'description',
    },
  ];

  const validateNoSpecialChars = (_, value) => {
    const regex = /[!@#$%{}^&*()_+=;:\\|,<>?]/; // Allows only alphanumeric characters and spaces
    if (value && regex.test(value)) {
      return Promise.reject(new Error('No special characters allowed!'));
    }
    return Promise.resolve();
  };

  const validateDomainInput = (_, value) => {
    if (!value) return Promise.resolve();

    const specialCharsRegex = /[!@#$%{}^&*()_+=;:'"\\|,<>/?]/;
    if (specialCharsRegex.test(value)) {
      return Promise.reject('Domain cannot contain special characters.');
    }

    if (value.startsWith('.') || value.endsWith('.')) {
      return Promise.reject('Domain cannot start or end with a dot.');
    }

    if (/\.\./.test(value)) {
      return Promise.reject('Domain cannot contain consecutive dots.');
    }

    if (value.length > 253) {
      return Promise.reject('Domain is too long (max 253 characters).');
    }

    const labels = value.split('.');

    if (labels.length > 5) {
      return Promise.reject('Domain has too many subdomains (max 5 levels).');
    }

    for (const label of labels) {
      if (label.length === 0 || label.length > 63) {
        return Promise.reject('Each part of the domain must be 1-63 characters.');
      }
      if (!/^[a-zA-Z0-9-]+$/.test(label)) {
        return Promise.reject('Domain parts can only contain letters, numbers, and hyphens.');
      }
      if (label.startsWith('-') || label.endsWith('-')) {
        return Promise.reject('Domain parts cannot start or end with a hyphen.');
      }
    }

    return Promise.resolve();
  };

  if (isUpdate) {
    if (tagType === 'Measurement') {
      initialValuesMeasurement = {
        advertiser: details.advertiser,
        appId: details.appId,
        domain: domainLookUp(details.advertiser),
        ecommerce: details.ecommerce ? details.ecommerce : false,
        name: details.name,
        platform: details.cart,
        signups: details.signups ? details.signups : false,
        dspTracking: details.dspTracking ? details.dspTracking : false,
        orgAdvertiserId: orgAdvertiserId,
        // s3_pv: details.s3_pv ? details.s3_pv : 'generic',
        // s3_tr: details.s3_tr ? details.s3_tr : 'generic-tr',
        thirdPartyTag: details.thirdPartyTag
          ? details.thirdPartyTag
          : {
              onTransaction: [
                {
                  type: '',
                  tag: '',
                },
              ],
            },
      };
    } else if (tagType === 'Publisher') {
      initialValuesMeasurement = {
        advertiser: details.advertiser,
        appId: details.appId,
        domainUrl: '',
        name: details.name,
        domainProtocol: 'https://',
        domainExtension: '.com',
        domain: domainLookUp(currentOrgId),
        isMulti: 'no',
      };

      if (details.publisherDomain) {
        const parsedUrl = new URL(details.publisherDomain ? details.publisherDomain : '');

        const extenstion =
          '.' + parsedUrl.hostname.split('.')[parsedUrl.hostname.split('.').length - 1];

        initialValuesMeasurement.domainUrl = parsedUrl.hostname.replace(extenstion, '');
        initialValuesMeasurement.domainExtension = parsedUrl.protocol + '//';
        initialValuesMeasurement.domainExtension = extenstion;
      }

      if (details.type && details.type.length) {
        if (details.type.length > 1) initialValuesMeasurement.isMulti = 'yes';
      }
    } else if (tagType === 'Impression') {
      initialValuesMeasurement = {
        dsp: details.impressionEnvironments ? details.impressionEnvironments : details.dsp,
        name: details.name,
        appId: details.appId,
        advertiser: details.advertiser,
      };
    }
  } else {
    if (tagType === 'Measurement') {
      initialValuesMeasurement = {
        ecommerce: false,
        signups: false,
        advertiser: currentOrgId,
        appId: v4(),
        domain: domainLookUp(currentOrgId),
        dspTracking: !!(orgAdvertiserId && orgAdvertiserId !== 'NEXXEN'),
        orgAdvertiserId: orgAdvertiserId,
        // s3_pv: 'generic',
        // s3_tr: 'generic-tr',
        thirdPartyTag: formattedThirdPartyTag,
      };
    } else if (tagType === 'Publisher') {
      initialValuesMeasurement = {
        advertiser: currentOrgId,
        domainProtocol: 'https://',
        domainExtension: '.com',
        isMulti: 'no',
        domain: domainLookUp(currentOrgId),
      };
    }
  }

  const transformTagsForForm = (thirdPartyTags) => {
    if (!thirdPartyTags) return { tags: [] };

    const tagsArray = Object.entries(thirdPartyTags).flatMap(([event, tagArray]) =>
      tagArray.map((tag) => ({
        event,
        tagType: tag.type,
        value: tag.tag,
      }))
    );

    return { tags: tagsArray };
  };

  const transformFormValuesToTagState = (tags = []) => {
    return tags.reduce((acc, tag) => {
      if (!tag) return acc;
      const { event, tagType, value } = tag;
      if (event && tagType && tagType.trim() && value && value.trim()) {
        if (!acc[event]) acc[event] = [];
        acc[event].push({
          type: tagType.trim(),
          tag: resolveTagCodeUrl(value.trim()),
        });
      }
      return acc;
    }, {});
  };

  const handleValuesChange = (_, allValues) => {
    if (!allValues || !allValues.tags) return;
    const updatedTags = transformFormValuesToTagState(allValues.tags);
    setFormattedThirdPartyTag(updatedTags);

    const newPartiallyFilled = {};
    allValues.tags.forEach((tag, index) => {
      if (!tag) return;
      newPartiallyFilled[index] = !!(tag.value && tag.value.trim());
    });
    setPartiallyFilledRows(newPartiallyFilled);
  };

  const impressionsItems = (
    <>
      <Form.Item
        label="Impression Tag Name"
        name="name"
        rules={[{ required: true, message: 'Name Required' }]}
      >
        <Input placeholder="Enter tag name" />
      </Form.Item>
      {isUpdate && (
        <Form.Item
          label="App ID"
          name="appId"
          rules={[{ required: true, message: 'App ID Required' }]}
        >
          <Input placeholder="Enter App ID" />
        </Form.Item>
      )}
      <Form.Item
        label="Advertising Platform"
        name="dsp"
        rules={[
          {
            required: true,
            message: 'Advertising Platform Required',
          },
        ]}
      >
        <Select
          className="tags-form__select--full"
          placeholder="Select Advertising Platform"
          options={platforms}
        />
      </Form.Item>
      <Form.Item name="advertiser" hidden noStyle>
        <Input type="hidden" />
      </Form.Item>
    </>
  );

  const publisherDomainItems = (
    <>
      <Row gutter={[11, 11]}>
        <Col span={24}>
          <Form.Item
            label="Publisher Tag Name"
            name="name"
            hidden={isMultiTagType === 'exist'}
            rules={[
              () => ({
                validator(_, value) {
                  //If process with google ads is enabled then google ads customer id is required
                  if (isMultiTagType !== 'exist') {
                    if (value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(`Name Required`);
                  }
                  return Promise.resolve();
                },
              }),
            ]}
          >
            <Input placeholder="Enter tag name" />
          </Form.Item>
        </Col>
        <Col span={24}>
          <Form.Item
            name={'advertiser'}
            rules={[
              () => ({
                validator(_, value) {
                  //If process with google ads is enabled then google ads customer id is required
                  if (isMultiTagType !== 'exist') {
                    if (value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(`Advertiser Required`);
                  }
                  return Promise.resolve();
                },
              }),
            ]}
            label={'Advertiser'}
            hidden={isMultiTagType === 'exist'}
          >
            <Select
              placeholder="Select Advertiser"
              options={advertisers}
              className="tags-form__select--full"
            />
          </Form.Item>
        </Col>
        <Col span={24}>
          {isUpdate ? (
            <Form.Item
              label="App ID"
              name="appId"
              rules={[{ required: true, message: 'App ID Required' }]}
            >
              <Input placeholder="Enter App ID" />
            </Form.Item>
          ) : null}
        </Col>
        <Col span={24}>
          <Form.Item
            label="Domain"
            name="domainUrl"
            rules={[
              {
                required: true,
                message: 'Domain Required',
              },
              {
                max: 45,
                message: 'Maximum length is 45 characters',
              },
              { validator: validateDomainInput },
            ]}
          >
            <Input
              addonBefore={
                <Select
                  defaultValue={'https://'}
                  onChange={(val) => {
                    form.setFieldsValue({ domainProtocol: val });
                  }}
                >
                  <Option value="http://">http://</Option>
                  <Option value="https://">https://</Option>
                </Select>
              }
              addonAfter={
                <Select
                  defaultValue=".com"
                  onChange={(val) => {
                    form.setFieldsValue({ domainExtension: val });
                  }}
                >
                  <Option value=".com">.com</Option>
                  <Option value=".net">.net</Option>
                  <Option value=".edu">.edu</Option>
                  <Option value=".org">.org</Option>
                  <Option value=".gov">.gov</Option>
                  <Option value=".mil">.mil</Option>
                </Select>
              }
            />
          </Form.Item>
        </Col>
        <Col span={24}>
          <Form.Item name={'isMulti'} label={'Will the tag acts as a Measurement Tags?'}>
            <Radio.Group onChange={(val) => setIsMultiTagType(val.target.value)}>
              <Radio value={'no'}>No</Radio>
              <Radio value={'yes'}>Yes</Radio>
              {!isUpdate ? <Radio value={'exist'}>Yes, Use existing Measurement Tag</Radio> : null}
            </Radio.Group>
          </Form.Item>
        </Col>
        <Col span={24}>
          <Form.Item
            name={'measurementTag'}
            hidden={isMultiTagType !== 'exist'}
            rules={[
              () => ({
                validator(_, value) {
                  //If process with google ads is enabled then google ads customer id is required
                  if (isMultiTagType === 'exist') {
                    if (value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(`Measurement Tag Required`);
                  }
                  return Promise.resolve();
                },
              }),
            ]}
            label={'Which tag should be used?'}
          >
            <Select
              placeholder="Select Measurement Tags"
              options={measurementTags}
              className="tags-form__select--full"
              showSearch
              filterOption={(input, option) =>
                option.label.toLowerCase().includes(input.toLowerCase().trim())
              }
            />
          </Form.Item>
        </Col>
      </Row>
      <Form.Item name="domainProtocol" noStyle hidden>
        <Input type="hidden" />
      </Form.Item>
      <Form.Item name="domainExtension" noStyle hidden>
        <Input type="hidden" />
      </Form.Item>
      <Form.Item label="Tag Domain" name="domain" hidden noStyle>
        <Input type="hidden" />
      </Form.Item>
    </>
  );

  return (
    <div className="tags-page">
      {headerSubmit ? (
        <PageHeaderLayout
          title={isUpdate ? 'Update Tag' : 'Create New Tag'}
          titleIcon={<TagsOutlined />}
          rightContent={
            <Space>
              {/* Regenerate button commented out per new requirements */}
              {/* {isUpdate && onRegenerateTag && (
                <Button
                  shape="round"
                  loading={isRegenerating}
                  disabled={isRegenerating || isSubmitting}
                  onClick={() => setRegenerateModalVisible(true)}
                >
                  Regenerate Tag
                </Button>
              )} */}
              <Button shape="round" onClick={() => onCancel ? onCancel() : window.history.back()} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button
                shape="round"
                type="primary"
                loading={isSubmitting}
                disabled={isSubmitting}
                onClick={handleSubmit}
              >
                {isUpdate ? 'Update' : 'Create Tag'}
              </Button>
            </Space>
          }
        />
      ) : null}

      <PageContentLayout>
        {tagType === 'Measurement' && (
          <AppSpinner spinning={isSubmitting}>
            <Card className="tags-manager__card">
              <Form
                form={form}
                layout="vertical"
                name={'createTag'}
                initialValues={initialValuesMeasurement}
              >
                {/* Tag Name & Advertiser */}
                <Row gutter={[16, 16]}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name={'name'}
                      rules={[
                        { required: true, message: 'Name is required' },
                        {
                          max: 45,
                          message: 'Maximum length is 45 characters',
                        },
                        { validator: validateNoSpecialChars },
                      ]}
                      label={<span className="tags-manager__form-label">Tag Name</span>}
                    >
                      <Input placeholder="Enter tag name" maxLength={46} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name={'advertiser'}
                      rules={[{ required: true, message: 'Advertiser Required' }]}
                      label={<span className="tags-manager__form-label">Advertiser</span>}
                    >
                      <Select
                        placeholder="Select an advertiser"
                        options={advertisers}
                        showSearch
                        filterOption={(input, option) =>
                          option.label.toLowerCase().includes(input.toLowerCase().trim())
                        }
                        onChange={(val) => {
                          form.setFieldsValue({ domain: domainLookUp(val) });

                          // Update DSP advertiser ID state for selected advertiser (from develop branch)
                          const advertiserDspId = dspAdvertiserId(val);
                          setCurrentAdvertiserDspId(advertiserDspId);

                          // Auto-enable/disable DSP tracking for new tags based on advertiser's DSP ID
                          if (!isUpdate) {
                            if (advertiserDspId && advertiserDspId !== 'NEXXEN') {
                              setDspTrackingSetup(true);
                              form.setFieldsValue({ dspTracking: true });
                            } else {
                              setDspTrackingSetup(false);
                              form.setFieldsValue({ dspTracking: false });
                            }
                          }
                        }}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                {isUpdate && (
                  <Row gutter={[16, 16]}>
                    <Col xs={24} md={12}>
                      <Form.Item
                        label={<span className="tags-manager__form-label">App ID</span>}
                        name="appId"
                        rules={[{ required: true, message: 'App ID Required' }]}
                        extra={
                          <span className="tags-manager__help-text tags-manager__help-text--warning">
                            <span>⚠️</span>
                            Changing the App ID may affect tracking capabilities. Proceed with
                            caution.
                          </span>
                        }
                      >
                        <Input
                          placeholder="Enter App ID"
                          className="tags-manager__monospace-input"
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item
                        label={<span className="tags-manager__form-label">Tag Domain</span>}
                        name="domain"
                        rules={[{ required: true, message: 'Tag Domain Required' }]}
                      >
                        <Input placeholder="Enter tag domain" />
                      </Form.Item>
                    </Col>
                  </Row>
                )}

                {!isUpdate && (
                  <>
                    <Form.Item
                      label="App ID"
                      name="appId"
                      rules={[{ required: true, message: 'App ID Required' }]}
                      hidden
                      noStyle
                    >
                      <Input type="hidden" />
                    </Form.Item>
                    <Form.Item
                      label="Tag Domain"
                      name="domain"
                      rules={[{ required: true, message: 'Tag Domain Required' }]}
                      hidden
                      noStyle
                    >
                      <Input type="hidden" />
                    </Form.Item>
                  </>
                )}

                {/* Ecommerce Section */}
                <Divider />
                <Form.Item name={'ecommerce'} noStyle>
                  <input type="hidden" />
                </Form.Item>
                <ToggleFieldWithDescription
                  label="Ecommerce"
                  description="Will the tag measure ecommerce?"
                  checked={ecommerceSetup}
                  onChange={(val) => {
                    setEcommerceSetup(val);
                    form.setFieldsValue({ ecommerce: val });
                  }}
                />

                {ecommerceSetup && (
                  <div className="tags-manager__nested-content">
                    <Row gutter={[16, 16]}>
                      <Col span={24}>
                        <p className="tags-manager__help-text">
                          Enter your website URL to automatically detect the ecommerce platform
                        </p>
                        <Row gutter={[12, 12]}>
                          <Col flex="auto">
                            <Form.Item
                              name="siteScan"
                              rules={[{ validator: validateURL }]}
                              className="tags-manager__mb-0"
                            >
                              <Input
                                placeholder="https://www.yourstore.com"
                                onChange={(val) => {
                                  const inputValue = val.target.value;
                                  setSiteUrl(inputValue);
                                }}
                              />
                            </Form.Item>
                          </Col>
                          <Col flex="120px">
                            <Button
                              type="primary"
                              onClick={handleSiteScan}
                              disabled={!validUrl}
                              block
                              className="tags-manager__scan-btn"
                            >
                              Scan Site
                            </Button>
                          </Col>
                        </Row>
                      </Col>
                      <Col span={24}>
                        {cartDetectionLoading ? (
                          <LoadingOutlined />
                        ) : (
                          detectedCarts &&
                          detectedCarts.map((cart) => (
                            <Alert
                              key={cart}
                              message={`Website is using ${cart} cart`}
                              type="success"
                              showIcon
                              action={
                                <Button
                                  size="small"
                                  type="primary"
                                  onClick={() => handleUsePlatform(cart)}
                                >
                                  Use {cart}
                                </Button>
                              }
                              className="tags-manager__alert-spaced"
                            />
                          ))
                        )}
                      </Col>
                      <Col span={24}>
                        <p className="tags-manager__help-text">
                          Which shopping cart platform is being used?
                        </p>
                        <Form.Item
                          name={'platform'}
                          rules={[
                            () => ({
                              validator(_, value) {
                                if (ecommerceSetup) {
                                  if (value) {
                                    return Promise.resolve();
                                  }
                                  return Promise.reject(`Cart Required`);
                                }
                                return Promise.resolve();
                              },
                            }),
                          ]}
                        >
                          <Select
                            placeholder="Select a platform"
                            options={carts}
                            value={selectedCart}
                            onChange={(val) => {
                              setSelectedCart(val);
                              form.setFieldsValue({ platform: val });
                            }}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  </div>
                )}

                {/* Sign-ups Section */}
                <Divider />
                <Form.Item name={'signups'} noStyle>
                  <input type="hidden" />
                </Form.Item>
                <ToggleFieldWithDescription
                  label="Sign-ups"
                  description="Will the tag measure sign-ups?"
                  checked={signupsSetup}
                  onChange={(val) => {
                    setSignupsSetup(val);
                    form.setFieldsValue({ signups: val });
                  }}
                />

                {/* DSP Section */}
                <Divider />
                <Form.Item name={'dspTracking'} noStyle>
                  <input type="hidden" />
                </Form.Item>
                <Form.Item name={'orgAdvertiserId'} noStyle>
                  <input type="hidden" />
                </Form.Item>
                {(() => {
                  const hasDspId =
                    currentAdvertiserDspId !== null &&
                    currentAdvertiserDspId !== '' &&
                    currentAdvertiserDspId !== undefined;
                  const isNexxen = currentAdvertiserDspId === 'NEXXEN';
                  const isDspDisabled = !hasDspId || isNexxen;

                  const dspTooltip = isNexxen
                    ? 'DSP tracking is not supported for Nexxen advertisers.'
                    : 'Update your DSP Advertiser ID in the Org Setting Section';

                  return (
                    <ToggleFieldWithDescription
                      label="DSP"
                      description="Will the tag have DSP-level tracking?"
                      checked={dspTrackingSetup}
                      disabled={isDspDisabled}
                      disabledTooltip={isDspDisabled ? dspTooltip : undefined}
                      onChange={(val) => {
                        if (!isDspDisabled) {
                          setDspTrackingSetup(val);
                          form.setFieldsValue({ dspTracking: val });
                        }
                      }}
                    />
                  );
                })()}

                {/* {dspTrackingSetup && (
                <>
                  <Row gutter={[18, 18]}>
                    <Col xs={24} md={12}>
                      <Form.Item
                        name={'s3_pv'}
                        label={'(s3.pv)'}
                        tooltip="Enter the ID for page views. Defaults to 'generic' if not provided."
                        hidden={!dspTrackingSetup}
                      >
                        <Input placeholder="generic" disabled={userIsTrafficker === false} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item
                        name={'s3_tr'}
                        label={'(s3.tr)'}
                        tooltip="Enter ID for transactions. Defaults to 'generic-tr' if not provided."
                        hidden={!dspTrackingSetup}
                      >
                        <Input placeholder="generic-tr" disabled={userIsTrafficker === false} />
                      </Form.Item>
                    </Col>
                  </Row>
                </>
              )} */}

                {/* Third Party Tags Section */}
                <Divider />
                <Text className="tags-manager__section-title">Third Party Tags</Text>
                <div className="tags-manager__mb-4">
                  <Text className="tags-manager__toggle-field-description">
                    Third party tags are custom tracking scripts that execute when specific events
                    occur on your website. <Link onClick={showMacrosModal}>View Macros</Link>
                  </Text>
                </div>

                <Form
                  form={formTag}
                  name="dynamic_form"
                  autoComplete="off"
                  initialValues={transformTagsForForm(formattedThirdPartyTag)}
                  onValuesChange={handleValuesChange}
                >
                  <Form.List name="tags">
                    {(fields, { add, remove }) => (
                      <>
                        {fields.map(({ key, name, ...restField }, index) => (
                          <div key={key} className="tags-manager__third-party-card">
                            <div className="tags-manager__third-party-header">
                              <Text className="tags-manager__form-label">Tag {index + 1}</Text>
                              {fields.length > 1 && (
                                <Button
                                  type="text"
                                  danger
                                  icon={<MinusCircleOutlined />}
                                  onClick={() => remove(name)}
                                  size="small"
                                />
                              )}
                            </div>
                            <Row gutter={[16, 16]}>
                              <Col xs={24} md={12}>
                                <Form.Item
                                  {...restField}
                                  name={[name, 'event']}
                                  label={<span className="tags-manager__form-label">Event</span>}
                                  required={!!partiallyFilledRows[index]}
                                  dependencies={[['tags', name, 'value']]}
                                  rules={[
                                    ({ getFieldValue }) => ({
                                      validator(_, value) {
                                        const tagCode = getFieldValue(['tags', name, 'value']);
                                        if (tagCode && tagCode.trim() && !value) {
                                          return Promise.reject(new Error('Event is required'));
                                        }
                                        return Promise.resolve();
                                      },
                                    }),
                                  ]}
                                >
                                  <Select placeholder="Select event">
                                    <Option value="onTransaction">On Transaction</Option>
                                    <Option value="onAddToCart">On Add To Cart</Option>
                                    <Option value="onRemoveFromCart">On Remove From Cart</Option>
                                    <Option value="onPageview">On Pageview</Option>
                                  </Select>
                                </Form.Item>
                              </Col>
                              <Col xs={24} md={12}>
                                <Form.Item
                                  {...restField}
                                  name={[name, 'tagType']}
                                  label={<span className="tags-manager__form-label">Tag Type</span>}
                                  required={!!partiallyFilledRows[index]}
                                  dependencies={[['tags', name, 'value']]}
                                  rules={[
                                    ({ getFieldValue }) => ({
                                      validator(_, value) {
                                        const tagCode = getFieldValue(['tags', name, 'value']);
                                        if (tagCode && tagCode.trim() && !value) {
                                          return Promise.reject(new Error('Tag type is required'));
                                        }
                                        return Promise.resolve();
                                      },
                                    }),
                                  ]}
                                >
                                  <Select placeholder="Select tag type">
                                    <Option value="script">Script Tag</Option>
                                    <Option value="image">Image Pixel</Option>
                                  </Select>
                                </Form.Item>
                              </Col>
                              <Col span={24}>
                                <Form.Item
                                  {...restField}
                                  name={[name, 'value']}
                                  label={<span className="tags-manager__form-label">Tag Code</span>}
                                  rules={[{ required: false }, { validator: validateTagCode }]}
                                >
                                  <Input.TextArea
                                    placeholder="Paste your third party tag code here..."
                                    rows={3}
                                    className="tags-manager__monospace-textarea"
                                  />
                                </Form.Item>
                              </Col>
                            </Row>
                          </div>
                        ))}
                        <Button
                          type="dashed"
                          onClick={() => add()}
                          icon={<PlusOutlined />}
                          block
                          className="tags-manager__mt-2"
                        >
                          Add Another Third Party Tag
                        </Button>
                      </>
                    )}
                  </Form.List>
                </Form>

                {isUpdate && orgConfig.isTrafficker && (
                  <>
                    <Divider />
                    <Collapse ghost bordered={false} expandIconPosition="end">
                      <Collapse.Panel
                        header={
                          <Text className="tags-manager__section-title">Advanced Options</Text>
                        }
                        key="1"
                      >
                        {frictionlessTagLoading ? (
                          <div className="tags-manager__pt-4">
                            <Skeleton active paragraph={{ rows: 6 }} />
                          </div>
                        ) : (
                          <Row gutter={[16, 16]} className="tags-manager__pt-4">
                            <Col span={24}>
                              <div className="tags-manager__mb-2">
                                <Text className="tags-manager__form-label">Tag Overriding</Text>
                                <p className="tags-manager__help-text">
                                  Allows for overriding the parameters for tags installed on the
                                  site
                                </p>
                              </div>
                              {hasAppIdOverride && (
                                <Alert
                                  message="Invalid Field Name"
                                  description='Cannot use "appId" as an override field name. Please use a different field name.'
                                  type="error"
                                  showIcon
                                  className="tags-manager__mb-4"
                                />
                              )}
                              <div className="tags-manager__mt-4">
                                {tagOverrides.map((override, index) => (
                                  <Row
                                    key={override.id}
                                    gutter={[12, 12]}
                                    className="tags-manager__override-row"
                                  >
                                    <Col span={11}>
                                      <AutoComplete
                                        style={{ width: '100%' }}
                                        placeholder="Field name"
                                        value={override.field}
                                        options={OVERRIDE_FIELD_OPTIONS.map((opt) => ({
                                          value: opt,
                                        }))}
                                        filterOption={(inputValue, option) =>
                                          option.value
                                            .toLowerCase()
                                            .includes(inputValue.toLowerCase())
                                        }
                                        onChange={(val) => {
                                          const newOverrides = [...tagOverrides];
                                          newOverrides[index].field = val;
                                          setTagOverrides(newOverrides);
                                        }}
                                      />
                                    </Col>
                                    <Col span={11}>
                                      {(() => {
                                        const field = override.field;
                                        const valueOptions =
                                          field === 'environment' && carts
                                            ? carts.map((c) => ({ label: c.label, value: c.value }))
                                            : field === 'version'
                                              ? [
                                                  { label: '1', value: '1' },
                                                  { label: '2', value: '2' },
                                                ]
                                              : field === 'plugin'
                                                ? [
                                                    { label: 'googleAds', value: 'googleAds' },
                                                    { label: 'bingAds', value: 'bingAds' },
                                                  ]
                                                : [];

                                        if (valueOptions.length > 0) {
                                          return (
                                            <AutoComplete
                                              style={{ width: '100%' }}
                                              placeholder="Value"
                                              value={override.value}
                                              options={valueOptions}
                                              filterOption={(inputValue, option) =>
                                                option.value
                                                  .toLowerCase()
                                                  .includes(inputValue.toLowerCase())
                                              }
                                              onChange={(val) => {
                                                const newOverrides = [...tagOverrides];
                                                newOverrides[index].value = val;
                                                setTagOverrides(newOverrides);
                                              }}
                                            />
                                          );
                                        }

                                        return (
                                          <Input
                                            placeholder="Value"
                                            value={override.value}
                                            onChange={(e) => {
                                              const newOverrides = [...tagOverrides];
                                              newOverrides[index].value = e.target.value;
                                              setTagOverrides(newOverrides);
                                            }}
                                          />
                                        );
                                      })()}
                                    </Col>
                                    <Col span={2}>
                                      <Button
                                        type="text"
                                        danger
                                        icon={<MinusCircleOutlined />}
                                        onClick={() => {
                                          if (tagOverrides.length > 1) {
                                            setTagOverrides(
                                              tagOverrides.filter((o) => o.id !== override.id)
                                            );
                                          }
                                        }}
                                        disabled={tagOverrides.length === 1}
                                      />
                                    </Col>
                                  </Row>
                                ))}
                                <Button
                                  type="dashed"
                                  onClick={() =>
                                    setTagOverrides([
                                      ...tagOverrides,
                                      { id: v4(), field: '', value: '' },
                                    ])
                                  }
                                  icon={<PlusOutlined />}
                                  block
                                  className="tags-manager__mt-2"
                                >
                                  Add Override
                                </Button>
                              </div>
                            </Col>
                            <Col span={24}>
                              <Divider />
                              <ToggleFieldWithDescription
                                label="Disable Tag"
                                description="This will disable tracking for the tag"
                                checked={isTagDisabled}
                                onChange={(val) => setIsTagDisabled(val)}
                              />
                            </Col>
                            <Col span={24}>
                              <div className="tags-manager__mt-6">
                                <Text className="tags-manager__form-label">Custom Code</Text>
                                <p className="tags-manager__help-text">
                                  This allows the site the tag is installed in to trigger
                                  custom-made code
                                </p>
                                <div className="tags-manager__code-editor-wrap">
                                  <AceEditor
                                    mode="javascript"
                                    theme="tomorrow_night"
                                    value={customCode}
                                    onChange={handleCodeEditorChange}
                                    name="customCodeEditor"
                                    editorProps={{ $blockScrolling: true }}
                                    setOptions={{
                                      enableBasicAutocompletion: true,
                                      enableLiveAutocompletion: true,
                                      enableSnippets: true,
                                      showLineNumbers: true,
                                      tabSize: 2,
                                      fontSize: fontSize.button,
                                      showPrintMargin: false,
                                      showGutter: true,
                                      highlightActiveLine: true,
                                      wrap: true,
                                    }}
                                    className="tags-manager__code-editor"
                                    style={{
                                      width: '100%',
                                      height: '300px',
                                    }}
                                  />
                                </div>
                              </div>
                            </Col>
                          </Row>
                        )}
                      </Collapse.Panel>
                    </Collapse>
                  </>
                )}
              </Form>
            </Card>
          </AppSpinner>
        )}

        {tagType === 'Impression' && (
          <AppSpinner spinning={isSubmitting}>
            <Form
              form={form}
              layout="vertical"
              name={'createImpressionsTag'}
              initialValues={initialValuesMeasurement}
            >
              <Row gutter={[24, 24]}>
                <Col span={24}>
                  {onCard ? (
                    <Card title={<Text strong>Impressions Tag</Text>}>{impressionsItems}</Card>
                  ) : (
                    impressionsItems
                  )}
                </Col>
              </Row>
            </Form>
          </AppSpinner>
        )}

        {tagType === 'Publisher' && (
          <AppSpinner spinning={isSubmitting}>
            <Form
              form={form}
              layout="vertical"
              name={'createPublisherTag'}
              initialValues={initialValuesMeasurement}
            >
              <Row gutter={[16, 16]}>
                <Col span={24}>
                  {onCard ? (
                    <Card title={<Text strong>Publisher Domain</Text>}>{publisherDomainItems}</Card>
                  ) : (
                    publisherDomainItems
                  )}
                </Col>
              </Row>
            </Form>
          </AppSpinner>
        )}
        {!headerSubmit ? (
          <Col span={24}>
            <Row align="end" className="tags-manager__footer-row">
              <Space>
                <Button shape="round" onClick={() => onCancel ? onCancel() : window.history.back()} disabled={isSubmitting}>
                  Cancel
                </Button>
                <Button
                  shape="round"
                  type="primary"
                  loading={isSubmitting}
                  disabled={isSubmitting}
                  onClick={handleSubmit}
                >
                  {isUpdate ? 'Update' : 'Create Tag'}
                </Button>
              </Space>
            </Row>
          </Col>
        ) : null}
      </PageContentLayout>
      <Modal
        title="Available Macros"
        wrapClassName="tags-modal"
        visible={macrosVisible}
        onCancel={() => setMacrosVisible(false)}
        footer={null}
        width={'80%'}
        centered
      >
        <>
          <Select
            defaultValue="onTransaction"
            className="tags-manager__macros-select"
            onChange={handleChange}
          >
            {Object.keys(macrosData).map((event) => (
              <Option key={event} value={event}>
                {event}
              </Option>
            ))}
          </Select>
          <Table
            aria-label="Available macros"
            dataSource={macrosData[selectedEvent]}
            columns={columns}
            pagination={false}
            rowKey="key"
          />
        </>
      </Modal>
      {/* Regenerate modal commented out per new requirements */}
      {/* <Modal
        title="Regenerate Tag"
        open={regenerateModalVisible}
        onCancel={() => setRegenerateModalVisible(false)}
        onOk={handleRegenerateTag}
        okText="Regenerate"
        confirmLoading={isRegenerating}
        okButtonProps={{ danger: true }}
      >
        <p>
          Are you sure you want to regenerate this tag? This will update the tag's generated code
          using the current form values.
        </p>
        <p>
          <strong>Note:</strong> The tag will be regenerated using the values currently entered in
          the form above.
        </p>
      </Modal> */}
    </div>
  );
};

TagsManagerView.propTypes = {
  onSubmit: PropTypes.func,
  providers: PropTypes.array,
  advertisers: PropTypes.array,
  currentOrgId: PropTypes.string,
  headerSubmit: PropTypes.bool,
  tagType: PropTypes.oneOf(['Measurement', 'Impression', 'Publisher']),
  isUpdate: PropTypes.bool,
};

export default TagsManagerView;
